import { deflate, inflate } from 'pako';
import { base64ToUint8Array, getLabel } from '../../../utils/utils';
import {
	ASSET_TYPE_OPTIONS,
	EMISSION_SCOPE_OPTIONS,
	ENERGY_SYSTEM_OPTIONS,
	EXTERNAL_OPTIONS,
	GWP_VALUES,
	INPUT_OPTIONS,
	OUTPUT_OPTIONS,
	PATHWAY_OPTIONS,
} from '../constants/cnp.constants';
import global from '../../../config/global';

export const _handleSortEnergyAssets = (assets) => {
	const indexMap = {};

	assets.forEach((asset) => {
		const hasAlt = assets.find((a) => a.baselineId === asset._id);
		asset['hasAlt'] = hasAlt ? true : false;
	});

	let sortedAssets = ENERGY_SYSTEM_OPTIONS.flatMap(({ key }) =>
		assets
			.filter((asset) => asset.energySystem === key)
			.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate)),
	);

	for (let asset of sortedAssets.filter((asset) => asset.assetInfo?.configuration)) {
		const { assetType, energySystem, assetName } = asset;

		if (!indexMap[energySystem]) indexMap[energySystem] = {};

		const name = energySystem === 'fuel' || assetType === 'other' ? assetName : assetType;

		indexMap[energySystem][name] = (indexMap[energySystem][name] || 0) + 1;
		asset['index'] = indexMap[energySystem][name];
	}

	for (let asset of sortedAssets.filter((asset) => !asset.assetInfo?.configuration && asset.baselineId)) {
		const { baselineId } = asset;
		asset['index'] = sortedAssets.find((ass) => ass._id === baselineId)?.index;
	}

	return orderByBaselineAndAlternativePair(sortedAssets);
};

const orderByBaselineAndAlternativePair = (assets) => {
	const alternativeMap = new Map();
	const baselineItems = [];
	const others = [];

	for (const item of assets) {
		if (item.baselineId) {
			alternativeMap.set(item.baselineId, (alternativeMap.get(item.baselineId) || []).concat(item));
		} else {
			baselineItems.push(item);
		}
	}
	const sortedArray = [];

	for (const baseline of baselineItems) {
		sortedArray.push(baseline);
		if (alternativeMap.has(baseline._id)) {
			sortedArray.push(...alternativeMap.get(baseline._id));
		}
	}

	return sortedArray;
};

export const _handleEnergyAssetHelper = (asset) => {
	if (!asset) return {};
	let {
		_id,
		energySystem,
		assetType,
		assetName,
		assetInfo: {
			capacity = 0,
			noOfUnit = 0,
			opHr,
			coefficient,
			efficiency,
			input = 0,
			output,
			outputName,
			pathway,
			pathwayName,
			scope,
			configuration,
			externalInfo,
			fuelInfo,
		},
		isSelected,
		index,
		mainEquipmentCost = 0,
		inputCost = 0,
		fixedValue = 0,
		variableValue = 0,
		othersValue = 0,
		reductionTarget = 1,
		hasAlt,
		baselineId,
	} = asset;

	const es = getLabel(ENERGY_SYSTEM_OPTIONS, energySystem);
	const at = energySystem === 'fuel' || assetType === 'other' ? assetName : getLabel(ASSET_TYPE_OPTIONS, assetType);

	const i = (() => {
		if (input === 'fuel' && fuelInfo) return fuelInfo?.fuelName;
		if (input === 'external' && externalInfo) {
			const { resource, resourceName } = externalInfo;
			return resource === 'other' ? resourceName : getLabel(EXTERNAL_OPTIONS, resource);
		}
		return getLabel(INPUT_OPTIONS, input);
	})();

	const o = output === 'energy_carrier' || output === 'other' ? outputName : getLabel(OUTPUT_OPTIONS, output);

	const p = configuration ? null : pathway === 'other' ? pathwayName : getLabel(PATHWAY_OPTIONS, pathway);

	const s = getLabel(EMISSION_SCOPE_OPTIONS, scope);

	const config = configuration ? 'Baseline' : 'Alternative';

	const consumptionData =
		input === 'fuel'
			? fuelInfo?.consumptionData * (baselineId ? 1 - (reductionTarget / 100 || 1) : 1) || 0
			: input === 'external'
				? externalInfo?.consumptionData * (baselineId ? 1 - (reductionTarget / 100 || 1) : 1) || 0
				: 0;

	const { co2GWP, ch4GWP, n2oGWP } = GWP_VALUES;

	const emissionFactor = (() => {
		if (input === 'external') return externalInfo?.emissionFactor || 0;
		if (input === 'fuel') {
			const co2 = fuelInfo?.isBiomass ? 0 : fuelInfo?.co2Factor * co2GWP;
			const ch4 = fuelInfo?.ch4Factor * ch4GWP;
			const n2o = fuelInfo?.n2oFactor * n2oGWP;
			return co2 + ch4 + n2o;
		}
		return 0;
	})();

	const ghgEmission = consumptionData * emissionFactor || 0;

	const capex = noOfUnit * capacity * mainEquipmentCost;
	const opex = consumptionData * inputCost * noOfUnit + (fixedValue + variableValue) * capacity + othersValue;

	return {
		_id,
		energySystem: es,
		assetType: at,
		isSelected,
		index,
		input: i,
		output: o,
		pathway: p,
		scope: s,
		configuration: config,
		consumptionData,
		emissionFactor,
		ghgEmission,
		inputCost,
		mainEquipmentCost,
		fixedValue,
		variableValue,
		othersValue,
		capex,
		opex,
		capacity,
		noOfUnit,
		opHr,
		coefficient,
		efficiency,
		hasAlt,
		baselineId,
	};
};

export const indexedNPVandLCOEData = (arr) => {
	const indexMap = {};
	return arr.map((d) => {
		if (!indexMap[d.energySystem]) indexMap[d.energySystem] = 0;
		indexMap[d.energySystem]++;
		return { ...d, index: indexMap[d.energySystem] };
	});
};

export const npvCalcData = (npv, totalCapex = 0, totalOpex = 0) => {
	if (!npv) return;

	const { production = 0, price = 0 } = npv;
	const revenue = production * price;
	const cashFlow = revenue - totalOpex;
	const paybackPeriod = totalCapex / cashFlow || 0;
	return {
		revenue,
		cashFlow,
		paybackPeriod,
	};
};

export const generateNPV = (npv, cashFlow = 0, totalCapex = 0) => {
	if (!npv) return;

	const { production = 0, interestRate = 0, price = 0, noOfYrs = 0, residualValue = 0 } = npv;

	if (!(production && interestRate && price && noOfYrs > 0)) return;

	const arr = new Array(noOfYrs + 1);
	let prevNPV = -totalCapex;

	for (let i = 0; i <= noOfYrs; i++) {
		const discountFactor = 1 / Math.pow(1 + interestRate / 100, i);
		const cFlow = i === 0 ? 0 : cashFlow * discountFactor;
		const residual = i === Number(noOfYrs) ? discountFactor * residualValue : 0;

		const npv = prevNPV + cFlow + residual;
		arr[i] = { discountFactor, cFlow, residual, npv };
		prevNPV = npv;
	}

	return arr;
};

export const generateLCOE = (lcoe, totalOpex = 0, totalCapex = 0) => {
	if (!lcoe) return;

	const { interestRate = 0, noOfYrs = 0, annualProduction = 0, residualValue = 0, production = 0 } = lcoe;

	if (!(interestRate > 0 && noOfYrs > 0 && residualValue && production)) return;

	const arr = new Array(noOfYrs + 1);
	let accumulatedOpex = 0,
		accumulatedProduction = 0;

	for (let i = 0; i <= noOfYrs; i++) {
		const discountFactor = 1 / Math.pow(1 + interestRate / 100, i);
		const residual = discountFactor * residualValue;
		const opex = i === 0 ? 0 : totalOpex * discountFactor;
		const prod = i === 0 ? 0 : annualProduction * discountFactor;

		accumulatedOpex += opex;
		accumulatedProduction += prod;

		const lcoe = i === 0 ? 0 : (totalCapex + accumulatedOpex - residual) / accumulatedProduction;

		arr[i] = { discountFactor, residual, opex, production: prod, lcoe };
	}

	return arr;
};

export const componentToHtmlString = (coms, getHTMLString) => {
	let result = {};
	for (const key in coms) {
		const com = coms[key];
		const htmlString = getHTMLString(com);

		result[key] = htmlString;
	}
	return result;
};

export const htmlstringToBinary = (htmlString) => new TextEncoder().encode(htmlString);

export const binaryToCompressedBase64 = (binary) => {
	const compressed = deflate(binary);
	const binaryString = Array.from(compressed, (byte) => String.fromCharCode(byte)).join('');
	return btoa(binaryString);
};

export const htmlToCompressedBase64 = (html) => {
	const binary = htmlstringToBinary(html);
	const base64 = binaryToCompressedBase64(binary);
	return base64;
};

export const getCompressedBinaries = (contents) => {
	let compressedBiniaries = {};
	for (const key in contents) {
		const binary = htmlstringToBinary(contents[key]);
		const base64 = binaryToCompressedBase64(binary);
		compressedBiniaries[key] = base64;
	}
	return compressedBiniaries;
};

export const extractImgsFromHTML = (htmlString, newPath) => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlString, 'text/html');
	const imgElements = doc.getElementsByTagName('img');
	const newFiles = [];
	const existingFiles = [];

	for (const img of imgElements) {
		const src = img.getAttribute('src');
		const isTemp = src?.startsWith(global.baseUrl + 'uploads/temp/report/');
		const isExistingFile = src?.startsWith(global.baseUrl + `uploads/${newPath}/`);

		if (isExistingFile) {
			const fileName = src.split(`/${newPath}/`)?.[1]?.split(/[?#]/)?.[0];
			existingFiles.push(fileName);
		}

		if (src && isTemp) {
			// replace temp path with new path
			const regex = /temp\/report/g;
			img.setAttribute('src', src.replace(regex, newPath));

			const fileName = src.split('/temp/report/')?.[1]?.split(/[?#]/)?.[0];
			newFiles.push(fileName);
		}
	}

	const serializer = new XMLSerializer();
	const newHtmlString = serializer.serializeToString(doc.body);

	return { newFiles, existingFiles, newHtmlString };
};

export const getComKey = (name) => name?.split(' ')?.[0] || null;

export const buildMetadataForCustomReport = (metadata, componentList, contentsFromStatic, reportId) => {
	let modified = [...(componentList || [])];
	metadata.newImageFiles = {};
	metadata.existingImageFiles = {};

	modified.forEach((component) => {
		const comKey = getComKey(component.name);
		const content = component.content ? component.content : contentsFromStatic[comKey];

		const newPath = `report/${reportId}/${getComKey(component.name)}`;
		const { newFiles, existingFiles, newHtmlString } = extractImgsFromHTML(content, newPath);
		if (newFiles?.length) {
			metadata.newImageFiles[`uploads/${newPath}/`] = newFiles;
		}
		if (existingFiles?.length) {
			metadata.existingImageFiles[`uploads/${newPath}/`] = existingFiles;
		}
		component.content = htmlToCompressedBase64(newHtmlString);
	});

	metadata.componentList = modified;
	return metadata;
};

export const handleCompressedBase64Component = (component, contents) => {
	const { content, contentType, isDefault } = component;
	if (!isDefault && contentType === 'RichText') {
		const comKey = getComKey(component.name);
		if (!content && contents) {
			component.content = contents[comKey] || '';
			return;
		}
		const binary = base64ToUint8Array(content);
		const html = inflate(binary, { to: 'string' });
		component.content = html;
	}
};
